import OfficeLocationsField from '@/components/form/shared/OfficeLocationsField';
import {
  buildCommonValidationMessages,
  optionalNumberField,
  optionalOfficeLocationsField,
  optionalString,
  requiredString,
  validateOfficeLocationsRefine,
  withMaxLength,
} from '@/utils/validation/schemaHelpers';

import type { FieldDef, FieldGroup } from './types';
import type { DefaultOptionType } from 'antd/es/select';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

export type CompanyInfoFieldsOptions = {
  t: TFunction;
  title?: string;
  disabled?: boolean;
  industryOptions?: DefaultOptionType[];
  regionOptions?: DefaultOptionType[];
  companyId?: string;
  includeAddressField?: boolean;
  addressComponent?: ReactNode;
  companyNameField?: 'company_name' | 'name';
  fieldTestIds?: Partial<Record<'company_name' | 'name', string>>;
};

export function buildCompanyInfoFieldDefs(
  options: CompanyInfoFieldsOptions,
): FieldDef[] {
  const { t, disabled } = options;
  const messages = buildCommonValidationMessages(t);
  const companyNameField = options.companyNameField ?? 'company_name';
  const companyNameTestId = options.fieldTestIds?.[companyNameField];

  const fields: FieldDef[] = [
    {
      name: companyNameField,
      validation: requiredString(messages.required),
      ui: {
        type: 'text',
        label: t('COMPANY_NAME_TITLE'),
        componentProps: {
          disabled: !!disabled,
          ...(companyNameTestId ? { 'data-testid': companyNameTestId } : {}),
        },
      },
    },
    {
      name: 'city',
      validation: optionalString(),
      ui: {
        type: 'places',
        label: t('LOCATION_CITY_LABEL'),
        placeholder: t('LOCATION_CITY_PLACEHOLDER'),
      },
    },
    {
      name: 'office_locations',
      validation: optionalOfficeLocationsField(),
      ui: {
        type: 'custom',
        key: 'office-locations',
        render: ({ inputSize }) => (
          <OfficeLocationsField
            size={inputSize}
            companyId={options.companyId}
          />
        ),
      },
    },
    {
      name: 'industry',
      validation: optionalString(),
      ui: {
        type: 'select',
        label: t('INDUSTRIES_LABEL'),
        options: options.industryOptions,
      },
    },
    {
      name: 'region',
      validation: optionalString(),
      ui: {
        type: 'select',
        label: t('REGIONS_LABEL'),
        options: options.regionOptions,
      },
    },
    {
      name: 'notable_clients',
      validation: optionalString(),
      ui: {
        type: 'text',
        label: t('NOTABLE_CLIENTS_LABEL'),
      },
    },
    {
      name: 'number_of_employees',
      validation: optionalNumberField({ notValid: messages.notValid }),
      ui: {
        type: 'number',
        label: t('EMPLOYEES_LABEL'),
        colSpan: 12,
      },
    },
    {
      name: 'average_age',
      validation: optionalNumberField({ notValid: messages.notValid }),
      ui: {
        type: 'number',
        label: t('AVERAGE_AGE_LABEL'),
        colSpan: 12,
      },
    },
    {
      name: 'male_percentage',
      validation: optionalNumberField({ notValid: messages.notValid }),
      ui: {
        type: 'number',
        label: t('MALE_FEMALE_LABEL'),
        colSpan: 12,
      },
    },
    {
      name: 'dress_code',
      validation: optionalString(),
      ui: {
        type: 'text',
        label: t('DRESSCODE_LABEL'),
        colSpan: 12,
      },
    },
    {
      name: 'description',
      validation: optionalString(),
      ui: {
        type: 'textarea',
        label: t('ABOUT_COMPANY'),
        componentProps: { autoSize: { minRows: 4 } },
      },
    },
    {
      name: 'company_culture',
      validation: optionalString(),
      ui: {
        type: 'textarea',
        label: t('COMPANY_CULTURE_LABEL'),
        componentProps: { autoSize: { minRows: 4 } },
      },
    },
    {
      name: 'difference_description',
      validation: optionalString(),
      ui: {
        type: 'textarea',
        label: t('ABOUT_COMPANY_LABEL_2'),
        componentProps: { autoSize: { minRows: 4 } },
      },
    },
    {
      name: 'excluded_companies',
      validation: withMaxLength(optionalString(), 255, messages.maxLength(255)),
      ui: {
        type: 'text',
        label: t('EXCLUDED_COMPANIES_LABEL'),
      },
    },
  ];

  if (options.includeAddressField && options.addressComponent) {
    fields.splice(3, 0, {
      name: 'address',
      validation: withMaxLength(optionalString(), 255, messages.maxLength(255)),
      ui: {
        type: 'custom',
        key: 'address-field',
        label: t('QUESTION_TYPE_ADDRESS'),
        render: () => options.addressComponent,
      },
    });
  }

  return fields;
}

export function buildCompanyInfoGroup(
  options: CompanyInfoFieldsOptions,
): FieldGroup {
  const messages = buildCommonValidationMessages(options.t);
  return {
    key: 'company-info',
    title: options.title,
    fields: buildCompanyInfoFieldDefs(options),
    refine: (data, ctx) => {
      validateOfficeLocationsRefine(data, ctx, messages.required);
    },
  };
}
